import React, { useState } from 'react';
import {
	FingotiButton,
	FingotiModalActions,
	FingotiModalContent,
} from '@fingoti/components';
import { useSnackbar } from 'notistack';

import makeStyles from '@material-ui/core/styles/makeStyles';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import { DateTimePicker } from '@material-ui/pickers';

import { TokenScopeSelector } from './TokenScopeSelector';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		justifyContent: 'center',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		[theme.breakpoints.up('md')]: {
			width: '60%',
		},
	},
	formControl: {
		marginTop: theme.spacing(3),
	},
	scopesLabel: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(1),
		color: theme.palette.greyTwo.main,
	},
	buttonGroup: {
		display: 'flex',
		justifyContent: 'space-evenly',
		width: '100%',
		[theme.breakpoints.up('md')]: {
			width: '50%',
		},
	},
	button: {
		width: '42%',
	},
	expiry: {
		width: '100%',
		marginTop: theme.spacing(3),
	},
}));

export const UserTokenAdd = ({
	create,
	availableScopes,
	handleNewToken,
	setOpen,
}) => {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();
	const [name, setName] = useState('');
	// Default to every scope the caller holds — equivalent to the old behaviour
	// of minting a token with all role-granted scopes.
	const [scopes, setScopes] = useState(availableScopes);
	const [enableExpiry, setEnableExpiry] = useState(false);
	const [expiry, setExpiry] = useState(new Date());
	const [submitting, setSubmitting] = useState(false);

	const createToken = async (event) => {
		event.preventDefault();
		setSubmitting(true);
		try {
			await create({
				name,
				scopes,
				expiresOn: enableExpiry ? new Date(expiry).toISOString() : null,
			});
			handleNewToken();
		} catch (error) {
			enqueueSnackbar(error?.message ?? 'Failed to create token', {
				variant: 'error',
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<FingotiModalContent>
				<div className={classes.root}>
					<form className={classes.form}>
						<FormControl variant='outlined'>
							<TextField
								required
								id='tokenName'
								name='tokenName'
								variant='outlined'
								label='token name'
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</FormControl>
						<FormControl variant='outlined' className={classes.formControl}>
							<FormControlLabel
								control={
									<Switch
										checked={enableExpiry}
										onChange={() => setEnableExpiry(!enableExpiry)}
										name='enableExpiry'
										color='primary'
									/>
								}
								label='enable expiry?'
							/>
						</FormControl>
						<Collapse in={enableExpiry}>
							<FormControl variant='outlined' className={classes.expiry}>
								<DateTimePicker
									DialogProps={{ style: { zIndex: 1400 } }}
									value={expiry}
									hideTabs
									disablePast
									inputVariant='outlined'
									onChange={setExpiry}
									label='expiry date/time'
								/>
							</FormControl>
						</Collapse>
						<Typography variant='body2' className={classes.scopesLabel}>
							scopes
						</Typography>
						<TokenScopeSelector value={scopes} onChange={setScopes} />
					</form>
				</div>
			</FingotiModalContent>

			<FingotiModalActions>
				<div className={classes.buttonGroup}>
					<FingotiButton
						light
						type='submit'
						color='primary'
						disabled={submitting || name.trim() === ''}
						className={classes.button}
						onClick={createToken}>
						create token
					</FingotiButton>
					<FingotiButton
						light
						className={classes.button}
						onClick={() => setOpen(false)}>
						Cancel
					</FingotiButton>
				</div>
			</FingotiModalActions>
		</>
	);
};
